'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useMemo } from 'react';
import { stickToTop, Sticky } from 'react-unstuck';
import styled, { css, useTheme } from 'styled-components';
import { stickyRespondsTo } from '../../helpers/stickyRespondsTo.js';
import { stickyWithFullHeight } from '../../helpers/stickyWithFullHeight.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { getDisplayStyle } from '../../mixins/getDisplayStyle.js';
import { getFlexWidthStyle } from '../../mixins/getFlexWidthStyle.js';
import { spacing } from '../../mixins/spacing.js';
import { getSidebarGapStyle } from './mixins/getSidebarGapStyle.js';

const StyledSidebarCol = styled.div.withConfig({
  componentId: "sc-19ur3x6-0"
})(["max-height:100vh;", ";order:", ";margin-left:", ";", ";", ";", ";"], ({
  tocTopPadding
}) => tocTopPadding && css(["margin-top:-", ";"], spacing(tocTopPadding)), ({
  sidebarPosition
}) => sidebarPosition === 'left' ? 1 : 2, ({
  sidebarPosition
}) => sidebarPosition === 'left' ? 0 : 'auto', ({
  sidebarDisplay
}) => sidebarDisplay && getDisplayStyle(sidebarDisplay), ({
  sidebarWidth
}) => getFlexWidthStyle(sidebarWidth), ({
  sidebarGap,
  sidebarPosition
}) => getSidebarGapStyle(sidebarGap, sidebarPosition));
const StyledContent = styled.div.withConfig({
  componentId: "sc-19ur3x6-1"
})(["position:relative;order:", ";", ";"], ({
  sidebarPosition
}) => sidebarPosition === 'left' ? 2 : 1, ({
  contentWidth
}) => getFlexWidthStyle(contentWidth));
const CONTENT_SIDEBAR_ID = 'sidebar';
const StyledContainer = styled.div.withConfig({
  componentId: "sc-19ur3x6-2"
})(["display:flex;flex-wrap:wrap;width:100%;"]);
const ContentWithSidebar = /*#__PURE__*/withTitan(({
  sidebar,
  sidebarPosition = 'left',
  sidebarGap = {
    xs: 6,
    s: 10
  },
  sidebarWidth = {
    xs: 1,
    m: 5 / 16
  },
  sidebarDisplay,
  sidebarRef,
  contentWidth = {
    xs: 1,
    m: 'stretch'
  },
  contentRef,
  isSticky,
  tocTopPadding = 0,
  stickyRespondsToExcludes,
  children,
  className,
  testId = 'contentWithSidebar'
}, ref) => {
  const {
    spacing
  } = useTheme();
  const fullHeightRef = useRef(null);
  const fullHeightStickyBehavior = useMemo(() => stickyWithFullHeight(fullHeightRef, stickToTop, spacing[tocTopPadding]), [fullHeightRef, spacing, tocTopPadding]);
  return jsxs(StyledContainer, {
    ref: ref,
    className: className,
    "data-test-id": testId,
    children: [sidebar && jsx(StyledSidebarCol, {
      ref: sidebarRef,
      "data-test-id": `${testId}Sidebar`,
      id: "sidebar-col",
      sidebarDisplay: sidebarDisplay,
      sidebarGap: sidebarGap,
      sidebarPosition: sidebarPosition,
      sidebarWidth: sidebarWidth,
      tocTopPadding: tocTopPadding,
      children: isSticky ? jsx(Sticky, {
        behavior: fullHeightStickyBehavior,
        labels: {
          scrollTo: false,
          sidebar: true
        },
        respondsTo: stickyRespondsTo(['sidebar', ...(stickyRespondsToExcludes || [])]),
        children: jsx("div", {
          ref: fullHeightRef,
          children: sidebar
        })
      }) : sidebar
    }), jsx(StyledContent, {
      ref: contentRef,
      contentWidth: contentWidth,
      "data-test-id": `${testId}Content`,
      id: "content-col",
      sidebarPosition: sidebarPosition,
      children: children
    })]
  });
}, {
  name: 'ContentWithSidebar',
  memo: true
});

export { CONTENT_SIDEBAR_ID, ContentWithSidebar };
//# sourceMappingURL=ContentWithSidebar.js.map
